/**
 * @file chat-message.schema.ts
 * @description Define o schema Zod para a entidade 'Message'.
 * Cada documento na coleção 'chatMessages' representa uma única mensagem
 * enviada dentro de uma conversa.
 */

import { z } from 'zod';

// --- Helper Schemas & Enums ---

const MessageAuthorTypeEnum = z.enum([
  'USER',
  'PROVIDER',
  'SYSTEM',
]);

const MessageAuthorSchema = z.object({
  id: z.string(),
  type: MessageAuthorTypeEnum,
  displayName: z.string(),
  portraitUrl: z.string().url().nullable(),
  description: z.string().nullable(),
});

// Conteúdos possíveis da mensagem
const MessageContentText = z.object({
  type: z.literal('text'),
  text: z.string().min(1).max(5000),
});

const MessageContentMedia = z.object({
  type: z.literal('media'),
  mediaType: z.enum(['image', 'video', 'audio', 'file']),
  url: z.string().url(),
  filename: z.string().optional(),
  size: z.number().int().positive().optional(),
  mimeType: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationSeconds: z.number().positive().optional(),
  // Video transcoding fields
  transcodingStatus: z.enum(['PENDING', 'TRANSCODING', 'READY', 'FAILED']).optional(),
  transcodingJobId: z.string().optional(),
  transcodingError: z.string().optional(),
  transcodedUrls: z.record(z.string()).optional(), // '720p', '480p', etc.
});

const MessageContentSystem = z.object({
  type: z.literal('system'),
  code: z.string(), // ex: PARTICIPANT_ADDED
  payload: z.record(z.any()).optional(),
});

const MessageContentSchema = z.discriminatedUnion('type', [
  MessageContentText,
  MessageContentMedia,
  MessageContentSystem,
]);


// --- Main Message Schema ---

const MessageSchema = z.object({
  id: z.string().optional(), // Firestore doc id (fora do documento original)
  conversationId: z.string(),
  seq: z.number().int().positive().optional(), // novo campo (atribuído no backend)
  author: MessageAuthorSchema,
  senderId: z.string(),
  content: MessageContentSchema,
  readBy: z.array(z.string()).default([]),
  // Mensagem interna da equipe: visível apenas para agentes (INTERNAL/PROVIDER)
  // na conversa, NUNCA para a usuária. Filtrada no backend por role.
  internal: z.boolean().optional(),
  deliveredAt: z.date().optional(),
  readAt: z.date().optional(),
  deletedAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date().optional(),
  clientMessageId: z.string().optional(), // novo campo opcional para idempotência no envio pelo cliente
  /**
   * Onde a mensagem estava antes da migração para os canais (A146), quando
   * mudou de conversa ou de número. Para reverter por escrita.
   */
  migradaDe: z.object({ conversationId: z.string(), seq: z.number().int().positive().optional() }).optional(),
});

const MessageSummarySchema = MessageSchema.pick({
  id: true,
  senderId: true,
  content: true,
  createdAt: true,
  readBy: true,
  seq: true,
});


// --- Inferred TypeScript Types ---

type Message = z.infer<typeof MessageSchema>;
type MessageAuthor = z.infer<typeof MessageAuthorSchema>;
type MessageAuthorType = z.infer<typeof MessageAuthorTypeEnum>;
type MessageContent = z.infer<typeof MessageContentSchema>;
type MessageSummary = z.infer<typeof MessageSummarySchema>;


// --- Public Exports ---

export { MessageSchema, MessageAuthorSchema, MessageAuthorTypeEnum, MessageContentSchema, MessageSummarySchema };
export type {
  Message,
  MessageAuthor,
  MessageAuthorType,
  MessageContent,
  MessageSummary,
};